import { useCallback } from 'react'
import { useSession } from 'app/session'

/**
 * Custom hook to handle opening Helpscout Beacon chat
 * Identifies the user if logged in and opens the chat widget
 */
export function useHelpscoutChat() {
  const session = useSession()

  const openChat = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    // Ensure Beacon function exists (initialized by Layout components)
    // The initialization script creates a queue system, so calls will be queued if not ready
    if (!window.Beacon) {
      window.Beacon = function() {
        (window.Beacon.readyQueue = window.Beacon.readyQueue || []).push(arguments)
      }
    }

    // Identify user with email if logged in
    if (session.isLoggedIn && session.user?.email) {
      window.Beacon('identify', {
        name: session.user.full_name,
        email: session.user.email
      })
    }

    // Function to open and navigate using Beacon API
    // This is the most reliable method and works even after closing/reopening
    const openAndNavigate = () => {
      window.Beacon('open')
      setTimeout(() => {
        window.Beacon('navigate', '/ai-answers/')
      }, 300)
    }

    // Always use the Beacon API method as it's reliable regardless of modal state
    // The API method works on first open, after closing, and on subsequent opens
    if (window.Beacon.readyQueue && window.Beacon.readyQueue.length === 0) {
      // Beacon is ready, open immediately
      openAndNavigate()
    } else {
      // Wait for ready event if needed, but also try immediately
      window.Beacon('on', 'ready', () => {
        openAndNavigate()
        window.Beacon('off', 'ready')
      })
      // Try immediately in case it's already ready
      openAndNavigate()
    }
  }, [session])

  return { openChat }
}
